// First-party visit beacon for launch-time admin monitoring.
//
// Sends a lightweight "page view" beacon to the backend on every route change.
// The backend turns each beacon into an ntfy admin notification (bot/human,
// browser/OS, traffic source). This is NOT third-party analytics — it is the
// site owner's own request log, on par with nginx access logs — so it is not
// gated behind cookie consent. Keep it free of PII beyond what an access log
// already captures (path, referrer, user-agent, IP at the server).
//
// Delivery uses navigator.sendBeacon (survives page unload, never blocks
// navigation) with a fetch+keepalive fallback. Failures are swallowed.

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const ENDPOINT = `${API_BASE}/track/pageview`;

let firstView = true;        // becomes false after the first route in this load
let lastPath = '';           // previous in-app path, for "from → to" transitions

interface VisitPayload {
  path: string;
  title: string;
  entry: boolean;            // true on the fresh page load (session entry)
  referrer: string;          // document.referrer, only meaningful on entry
  from: string;              // previous in-app path on SPA navigation
}

const post = (payload: VisitPayload): void => {
  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
    // Fallback when sendBeacon is unavailable or rejected the payload.
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* noop */ });
  } catch { /* analytics must never break the app */ }
};

/** Report a page view / navigation. Call on every router location change. */
export const trackVisit = (path: string): void => {
  const entry = firstView;
  post({
    path,
    title: document.title,
    entry,
    referrer: entry ? document.referrer : '',
    from: entry ? '' : lastPath,
  });
  firstView = false;
  lastPath = path;
};
